/**
 * The diff surface. Ported from the #5 prototype, where all four modes were compared
 * side by side on real policy text.
 *
 * **Both renderer families ship, admin's choice at read time** — Gareth's call on #5:
 * hunked columns win on near-total rewrites because aligned paragraphs help, prose-inline
 * wins on single-clause edits. The diagnostic `lineDiffType: 'none'` mode is gone; it
 * existed only to demonstrate the whole-line-replacement failure mode and is strictly
 * worse than the rest. Evidence in the prototype branch's evidence/ directory.
 */
import { Alert, Box, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { PolicyVersionState } from '../../../../types';
import { PierreDiff } from './PierreDiff';
import { changeStats, WordDiff } from './WordDiff';

export type DiffMode = 'pierre-split-word' | 'pierre-unified-word' | 'word-columns' | 'word-inline';

export const DIFF_MODES: { value: DiffMode; label: string; hint: string }[] = [
  {
    value: 'pierre-split-word',
    label: 'Diff · split',
    hint: '@pierre/diffs, side-by-side hunks, word highlighting inside changed lines. Best on near-total rewrites, where aligned paragraphs help.',
  },
  {
    value: 'pierre-unified-word',
    label: 'Diff · unified',
    hint: '@pierre/diffs, stacked hunks, word highlighting inside changed lines',
  },
  {
    value: 'word-columns',
    label: 'Prose · columns',
    hint: 'diffWordsWithSpace, two prose panes, no hunks or line numbers',
  },
  {
    value: 'word-inline',
    label: 'Prose · inline',
    hint: 'diffWordsWithSpace, one merged prose stream. Best on small clause edits.',
  },
];

/** DiffPane only needs text and a version number for the hunk labels. */
export type DiffSide = Pick<PolicyVersionState, 'version' | 'text'>;

interface Props {
  older: DiffSide;
  newer: DiffSide;
  mode: DiffMode;
  onModeChange: (mode: DiffMode) => void;
}

export const DiffPane = ({ older, newer, mode, onModeChange }: Props) => {
  const oldText = older.text ?? '';
  const newText = newer.text ?? '';
  const hasBlankSide = !oldText.trim() || !newText.trim();
  const stats = changeStats(oldText, newText);
  const changedPct = stats.total === 0 ? 0 : ((stats.added + stats.removed) / stats.total) * 100;

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 1,
          flexWrap: 'wrap',
        }}
      >
        <ToggleButtonGroup
          size="small"
          exclusive
          value={mode}
          onChange={(_, value) => value && onModeChange(value)}
        >
          {DIFF_MODES.map(diffMode => (
            <Tooltip key={diffMode.value} title={diffMode.hint}>
              <ToggleButton value={diffMode.value} sx={{ textTransform: 'none', px: 1.2 }}>
                {diffMode.label}
              </ToggleButton>
            </Tooltip>
          ))}
        </ToggleButtonGroup>
        {/* Fraction-of-text-changed is the honest companion to cosine: #9 measured the two
            at rank correlation -0.919, and this one is not a model's opinion. */}
        <Typography variant="caption" color="text.secondary">
          {stats.added} words added · {stats.removed} removed · {changedPct.toFixed(1)}% of text
          touched
        </Typography>
      </Box>

      {hasBlankSide ? (
        <Alert severity="warning" sx={{ mb: 1 }}>
          One side of this pair has <strong>no policy text</strong>. Nothing can be diffed, embedded
          or judged, and a blank version is never eligible.
        </Alert>
      ) : null}

      {mode === 'word-inline' || mode === 'word-columns' ? (
        <WordDiff
          oldText={oldText}
          newText={newText}
          mode={mode === 'word-inline' ? 'inline' : 'columns'}
        />
      ) : (
        <PierreDiff
          oldText={oldText}
          newText={newText}
          oldLabel={`v${older.version}`}
          newLabel={`v${newer.version}`}
          split={mode === 'pierre-split-word'}
          lineDiffType="word"
        />
      )}
    </Box>
  );
};
